import { useMemo, useState } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import { Banner, Button, Field, IconButton, Sheet, useToast } from '../ui'
import {
  refetchFailed, useCommitments, useDatedTasks, useEntryState, useFeed, useFlights, useLeaveDays, useLeaveYears, useShowings,
  useStays, useVisitors,
} from '../lib/queries'
import {
  useAddCommitment, useAddFlight, useAddStay, useAddVisitor, useRemoveCommitment, useRemoveFlight,
  useRemoveStay, useRemoveVisitor, useSetLeaveDay, useUpdateCommitment, useUpdateFlight, useUpdateStay, useUpdateVisitor,
} from '../lib/mutations'
import { attempt, useWriteError } from '../lib/useAction'
import {
  ADD_MAX_DAYS, addDays, countryHolidays, fmt, holidayOn, iso, periods, placeName, placeOf, yearFormMin, type Period,
} from '../lib/calendar'
import { leaveStats, stayLeaveChange } from '../lib/leaveDays'
import { leaveReading } from '../lib/yearSheet'
import { yearItems, pickTentativeSaved, type YearItem } from '../lib/yearItems'
import { defaultDirection, directionsFor, formFromRoute, routeFromForm, type Direction } from '../lib/flightRoute'
import { secondHomeAirports, secondHomeCountry, secondHomeOf, useProfile } from '../lib/profile'
import { yearStats } from '../lib/yearStats'
import { nowLine } from '../lib/yearNow'
import { homeName, useRegionsNow } from '../lib/regions'
import { useRegionHolidays } from '../lib/currentRegion'
import { foldQuietMonths, isQuietMonth, monthSpan } from '../lib/quietMonths'
import { learnedCountries, stayDraft, stayFromDraft } from '../lib/placeCountry'
import type { Stay, Commitment, Flight, FeedRow, Visitor } from '../lib/types'
import { Loading, Failed } from './Stub'
import { MonthRibbon } from '../components/MonthRibbon'
import { ScreenHead } from '../components/ScreenHead'
import { FormColumn, StayFields, YearAddSheet } from '../components/YearAddSheet'
import { RateQueue } from '../components/RateQueue'
import { EventSheet } from '../components/EventSheet'

/** One identity for "nothing yet", so the memos below do not recompute on a
 *  fresh empty array every render. */
const NONE_YET: never[] = []

type EditTarget =
  | { kind: 'stay'; row: Stay }
  | { kind: 'commitment'; row: Commitment }
  | { kind: 'flight'; row: Flight }
  | { kind: 'visitor'; row: Visitor }

const nextMonth = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  return new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 7)
}

export function Year() {
  const today = iso(new Date())
  const thisYearNum = Number(today.slice(0, 4))
  const nextYearNum = thisYearNum + 1
  const addMax = addDays(today, ADD_MAX_DAYS)
  const toast = useToast()
  // U1.3: the second home's name is the profile's. Above the early returns,
  // so a cold load reads it too.
  const profile = useProfile()
  const second = secondHomeOf(profile)
  // C2.1: what a `'Home'` period is called — the home region's name. The
  // bundled copy until the tables answer, so a cold load reads the same.
  const home = homeName(useRegionsNow(), profile)
  const holidayKey = useRegionHolidays()

  const [sheet, setSheet] = useState<null | 'add'>(null)
  const [edit, setEdit] = useState<EditTarget | null>(null)
  const [daySheet, setDaySheet] = useState<string | null>(null)
  // Y5 (WP 8.5): a commitment with an `entry_id` opens its real card instead
  // of the plain edit form — see `onEditItem`, below.
  const [openEntry, setOpenEntry] = useState<FeedRow | null>(null)
  // 2027 opens month by month automatically from three months out (1
  // October); the link can still override it either way for now — see
  // `wp-6.5-year-visitors.md` rule 4.
  const [show2027, setShow2027] = useState(today >= `${thisYearNum}-10-01`)
  // a2-31: next year's quiet months Doug has tapped open, by `YYYY-MM`.
  const [openedQuiet, setOpenedQuiet] = useState<ReadonlySet<string>>(() => new Set())

  const staysQ = useStays()
  const commitmentsQ = useCommitments()
  const flightsQ = useFlights()
  const leaveQ = useLeaveYears()
  // Leave is the marked days, as on the desktop (docs/web-plan Q1);
  // `leave_years` gives only the allowance. Not in `pending` or `error`: a
  // profile cached before this read existed has no `leave_days` yet, and the
  // rest of the Year should not wait on it — the number shows once it lands.
  const leaveDaysQ = useLeaveDays()
  const visitorsQ = useVisitors()
  const feedQ = useFeed()
  const entryStateQ = useEntryState()
  const showingsQ = useShowings()

  const stays = staysQ.data ?? NONE_YET
  const commitments = commitmentsQ.data ?? NONE_YET
  const flights = flightsQ.data ?? NONE_YET
  const leave = leaveQ.data ?? NONE_YET
  const visitors = visitorsQ.data ?? NONE_YET
  const feed = feedQ.data ?? NONE_YET
  const entryState = entryStateQ.data ?? NONE_YET
  // Y5: the row `onEditItem` opens `EventSheet` with, once a commitment's
  // `entry_id` resolves to one — the same lookup `Checklist.tsx` already
  // does for the same reason, not a second fetch path.
  const feedById = useMemo(() => new Map(feed.map(e => [e.id, e])), [feed])

  // Above the early returns, not below them — a hook called after the
  // `if (pending)` return is skipped on the first render and present on the
  // second, which is "Rendered more hooks than during the previous render".
  const addCommitmentM = useAddCommitment()
  const updateCommitmentM = useUpdateCommitment()
  const removeCommitmentM = useRemoveCommitment()
  const addStayM = useAddStay()
  const updateStayM = useUpdateStay()
  const removeStayM = useRemoveStay()
  const addFlightM = useAddFlight()
  const updateFlightM = useUpdateFlight()
  const removeFlightM = useRemoveFlight()
  const addVisitorM = useAddVisitor()
  const updateVisitorM = useUpdateVisitor()
  const removeVisitorM = useRemoveVisitor()
  const setLeaveM = useSetLeaveDay()
  const writeError = useWriteError(
    addCommitmentM, updateCommitmentM, removeCommitmentM,
    addStayM, updateStayM, removeStayM,
    addFlightM, updateFlightM, removeFlightM,
    addVisitorM, updateVisitorM, removeVisitorM,
    setLeaveM,
  )
  // null until the read lands: an unknown set is not an empty one.
  const marked = useMemo(
    () => (leaveDaysQ.data ? new Set(leaveDaysQ.data.map(l => l.day)) : null),
    [leaveDaysQ.data],
  )

  // Y2: birthdays stop being filtered out of the year, and a saved event
  // with exactly one future showing and nothing scheduled yet joins as a
  // tentative item.
  const commitmentsOnly = commitments.filter(c => !c.is_birthday)
  const birthdays = commitments.filter(c => c.is_birthday)
  const scheduledEntryIds = new Set(commitments.filter(c => c.entry_id).map(c => c.entry_id as string))
  const savedIds = new Set(entryState.filter(r => r.state === 'saved').map(r => r.entry_id))
  const byEntry = showingsQ.data?.byEntry
  const tentativeSaved = pickTentativeSaved(
    feed.filter(r => savedIds.has(r.id)).map(r => ({ row: { id: r.id, title: r.title }, showings: byEntry?.get(r.id) ?? [] })),
    scheduledEntryIds,
  )
  const tasks = useDatedTasks().data ?? NONE_YET

  // ---- the headline: this calendar year, which is what the stats count ----
  const yearPeriods = periods(stays, `${thisYearNum}-01-01`, `${thisYearNum}-12-31`)
  const stats = yearStats(yearPeriods, thisYearNum)
  // The desktop's count (`desk/screens/Year.tsx`): marked days in the year,
  // a day inside a provisional stay held rather than spent. Shown only for
  // a year with a `leave_years` row, as before.
  const leaveFor = (year: number) => {
    const allowance = leave.find(y => y.year === year)?.allowance
    if (allowance === undefined || !marked) return undefined
    const s = leaveStats([...marked], year, allowance, stays.filter(x => x.kind !== 'visit'))
    return { ...s, allowance, over: leaveReading(s.spent, s.held, allowance).over }
  }
  const thisYearLeave = leaveFor(thisYearNum)

  // ---- the whole displayed span, today through the end of next year —
  // computed once, whether or not 2027 is currently expanded, since the 2027
  // summary card's own counts need it regardless, and a period placed here
  // never needs re-deriving per month (`MonthRibbon`'s own header explains
  // why that matters for a long Berlin stretch). Opens at `today`, not this
  // week's Monday (WP 8.5, Doug: "it's showing me dates in the past"): a
  // stay already under way still gets its card, clipped to today onward by
  // `periods()`'s own walk (it never returns a period starting before the
  // `from` it was given), which is also why "you are here" still reads
  // right for one — `PeriodCard`'s `isNow` only needs `today` inside
  // `[p.start, p.end]`, never the stay's true start. ----
  const displayPeriods = periods(stays, today, `${nextYearNum}-12-31`)
  // Visitors are deliberately not part of this list — see `visitorItems`'s
  // own header in `yearItems.ts`. `MonthRibbon` draws them as their own
  // `VisitorCard`, from `visitors` directly, never as an item inside a
  // `PeriodCard`'s date-range slice.
  //
  // Filtered to `on >= today`, same reason: birthdays, flights and
  // commitments already past drop off the ribbon and the cards outright,
  // not just their period's start date — `yearItems()` itself only filters
  // by year (its own header says why: no `calendar.ts` import), so the day
  // floor is this caller's to apply.
  const displayItems = [thisYearNum, nextYearNum].flatMap(y => yearItems({
    commitments: commitmentsOnly, flights, birthdays, holidays: [], tasks, saved: tentativeSaved,
  }, y)).filter(i => i.on >= today)

  const pending = staysQ.isPending || commitmentsQ.isPending || flightsQ.isPending || leaveQ.isPending || visitorsQ.isPending
  const error = staysQ.error ?? commitmentsQ.error ?? flightsQ.error ?? leaveQ.error ?? visitorsQ.error

  // a2-30: the title, + and the account button stay on a failed read — the
  // account sheet is where the System view is, which is where Doug would
  // look next, and + still works (an add writes, it does not need the read).
  // Retry re-reads only the queries that failed.
  const head = (
    <ScreenHead
      className="yr-topbar"
      lead={<h1 className="screen-title">{thisYearNum}</h1>}
      trailing={<IconButton label="Add to the year" onClick={() => setSheet('add')}><Plus size={20} /></IconButton>}
    />
  )
  const addSheet = sheet === 'add' && (
    <YearAddSheet
      today={today} maxDate={addMax} stays={stays} onClose={() => setSheet(null)}
      onAddDay={(date, title) => void after(addCommitmentM.mutateAsync({ date, title }))}
      onAddStay={s => { markLeave(null, s); void after(addStayM.mutateAsync(s)) }}
      onAddFlight={f => void after(addFlightM.mutateAsync(f))}
      onAddVisitor={v => void after(addVisitorM.mutateAsync(v))}
    />
  )

  if (pending) return <Loading />
  if (error) {
    return (
      <>
        {head}
        {writeError && <Banner tone="bad">{writeError}</Banner>}
        <Failed error={error.message} onRetry={() => void refetchFailed()} />
        {addSheet}
      </>
    )
  }

  /** A stay write's leave marks, by the desktop's rule (`stayLeaveChange`),
   *  sent beside the stay's own write as the desktop sends them. A create
   *  marks even before `leave_days` has loaded (an upsert is idempotent);
   *  an edit or a delete needs to know which marks are the stay's, so it
   *  waits for the read rather than guessing. Returns the days cleared. */
  function markLeave(before: Stay | null, after: Pick<Stay, 'kind' | 'starts_on' | 'ends_on'> | null): string[] {
    if (before && !marked) return []
    const years = [before, after].flatMap(x => (x ? [Number(x.starts_on.slice(0, 4))] : []))
    const de = Object.assign({}, ...years.map(y => countryHolidays(holidayKey, y))) as Record<string, string>
    const diff = stayLeaveChange(before, after, marked ?? new Set(), de)
    for (const day of diff.clear) setLeaveM.mutate({ day, on: false })
    for (const day of diff.set) setLeaveM.mutate({ day, on: true })
    return diff.clear
  }

  function findCommitment(id: string) { return commitments.find(c => c.id === id) }
  function findFlight(id: string) { return flights.find(f => f.id === id) }
  function findVisitor(id: string) { return visitors.find(v => v.id === id) }

  async function after(work: Promise<unknown>) {
    if (await attempt(work)) setSheet(null)
  }

  async function afterEdit(work: Promise<unknown>) {
    if (await attempt(work)) setEdit(null)
  }

  // F7: removal is a swipe and a toast, never a confirmation dialog. Undo
  // re-adds the row through the same mutation the "Add" sheet uses.
  function removeCommitment(c: Commitment) {
    removeCommitmentM.mutate(c.id)
    toast({
      text: `Removed "${c.title}"`,
      action: { label: 'Undo', onClick: () => addCommitmentM.mutate({ date: c.on_date, title: c.title }) },
    })
  }

  function removeFlight(f: Flight) {
    removeFlightM.mutate(f.id)
    toast({
      text: `Removed ${f.code}`,
      action: {
        label: 'Undo',
        onClick: () => addFlightM.mutate({
          on_date: f.on_date, depart_at: f.depart_at, from_iata: f.from_iata,
          to_iata: f.to_iata, code: f.code ?? '',
        }),
      },
    })
  }

  // a2-24: a stay or a visitor deleted from its edit sheet gets the same
  // toast — a trip with a flag, dates and a label is the most expensive thing
  // on the page to retype. Undo puts back every field the row had, and the
  // leave marks the delete took with it, as the desktop's Undo does (a3-13).
  function removeStay(s: Stay) {
    const cleared = markLeave(s, null)
    removeStayM.mutate(s.id)
    toast({
      text: `Removed ${s.label ?? (s.kind === 'home2' ? placeName({ place: 'Home2', label: null }, second, home) : 'trip')}`,
      action: {
        label: 'Undo',
        onClick: () => {
          addStayM.mutate({
            kind: s.kind, starts_on: s.starts_on, ends_on: s.ends_on,
            label: s.label, provisional: s.provisional, country: s.country,
          })
          for (const day of cleared) setLeaveM.mutate({ day, on: true })
        },
      },
    })
  }

  function removeVisitor(v: Visitor) {
    removeVisitorM.mutate(v.id)
    toast({
      text: `Removed ${v.who}`,
      action: {
        label: 'Undo',
        onClick: () => addVisitorM.mutate({
          who: v.who, starts_on: v.starts_on, ends_on: v.ends_on, at_mine: v.at_mine, note: v.note,
        }),
      },
    })
  }

  // Y3/Y5: tap a commitment or a flight to edit, swipe left to remove —
  // nothing else on a card is Year's own to edit (a birthday and a holiday
  // are computed, a task is Lists', a saved event is the Shortlist's). WP
  // 8.5: a commitment written by `scheduleEntry` or "Add to calendar"
  // carries an `entry_id` — that one opens the real `EventSheet` (which
  // already has "Remove from the Year" via `EventActions`' `commitment`
  // prop) instead of the plain day-and-title form. A commitment whose entry
  // has since left `feed_lite` (past, cancelled) has nothing for the sheet
  // to show, so it falls back to the form rather than opening one with
  // nothing in it.
  function onEditItem(item: YearItem) {
    if (item.ref.table === 'commitments') {
      const c = findCommitment(item.ref.id)
      if (!c) return
      if (c.entry_id) {
        const entry = feedById.get(c.entry_id)
        if (entry) { setOpenEntry(entry); return }
      }
      setEdit({ kind: 'commitment', row: c })
    } else if (item.ref.table === 'flights') {
      const f = findFlight(item.ref.id)
      if (f) setEdit({ kind: 'flight', row: f })
    }
  }
  function onRemoveItem(item: YearItem) {
    if (item.ref.table === 'commitments') {
      const c = findCommitment(item.ref.id)
      if (c) removeCommitment(c)
    } else if (item.ref.table === 'flights') {
      const f = findFlight(item.ref.id)
      if (f) removeFlight(f)
    }
  }
  function onEditPeriod(p: Period) {
    if (!p.stayId) return
    const stay = stays.find(s => s.id === p.stayId)
    if (stay) setEdit({ kind: 'stay', row: stay })
  }
  function onEditVisitor(v: Visitor) {
    const row = findVisitor(v.id)
    if (row) setEdit({ kind: 'visitor', row })
  }

  const displayMonths: string[] = []
  for (let ym = today.slice(0, 7); ym <= `${thisYearNum}-12`; ym = nextMonth(ym)) displayMonths.push(ym)
  const y2027Months: string[] = []
  if (show2027) for (let ym = `${nextYearNum}-01`; ym <= `${nextYearNum}-12`; ym = nextMonth(ym)) y2027Months.push(ym)
  // a2-31 — Doug chose: runs of months with nothing planned fold into one
  // line ("Feb–May 2027 · nothing planned"), tapped to open them.
  // `quietMonths.ts` says what "nothing planned" is, and why birthdays do
  // not count. Next year only: this year's remaining months are close
  // enough that an empty one is worth seeing as empty.
  const y2027Slots = foldQuietMonths(
    y2027Months,
    ym => isQuietMonth(ym, { periods: displayPeriods, visitors, items: displayItems }),
    openedQuiet,
  )
  const birthdaysIn = (months: string[]) =>
    displayItems.filter(i => i.kind === 'birthday' && months.includes(i.on.slice(0, 7))).length
  const now = nowLine(displayPeriods, today, second, home)

  return (
    <>
      {/* WP 10.1: the year and its two buttons are the row; the stats line
          moved out from under the title to the full width below it. */}
      {head}
      {/* Each clause is its own `nowrap` span: on a narrow phone the line
          wraps between clauses, at a " · ", rather than splitting "leave
          days left" across two lines with "left" orphaned alone. */}
      {/* a2-29 — Doug chose: the header reads "Berlin 6 more days · next
          Wroclaw, Fri 9 Oct" (away: "Wroclaw 2 more days · back Sun 11
          Oct"), the leave figure after it. The whole-year totals it used to
          lead with move to a quieter line under it rather than going: they
          are the phone's only place for them. */}
      <div className="ystats">
        {now && (
          <>
            <span><b>{now.here}</b> {now.left}</span>
            {' · '}
            <span>{now.after}</span>
          </>
        )}
        {thisYearLeave && (
          <>
            {now && ' · '}
            {thisYearLeave.over > 0
              ? <span><b>{thisYearLeave.over}</b> {thisYearLeave.over === 1 ? 'day' : 'days'} over your {thisYearLeave.allowance} leave</span>
              : <span><b>{thisYearLeave.free}</b> leave days left</span>}
            {thisYearLeave.held > 0 && <>{' · '}<span><b>{thisYearLeave.held}</b> held by options</span></>}
          </>
        )}
      </div>
      <div className="ystats ystats--year">
        <span>{thisYearNum}: {stats.berlin} days in {home}</span>
        {' · '}
        <span>{stats.home2 + stats.travel} away</span>
      </div>
      {writeError && <Banner tone="bad">{writeError}</Banner>}

      <div className="legend">
        <span><i style={{ background: 'var(--paper-2)', border: '1px solid var(--line-2)' }} />{home}</span>
        <span><i style={{ background: 'var(--p-travel)' }} />Away</span>
        <span><i style={{ background: 'var(--warn)' }} />Public holiday</span>
        <span><i style={{ background: 'var(--p-visit)' }} />Visitor at yours</span>
      </div>

      {/* WP 8.4's "rate what you saw", moved here from the Shortlist's Went
          tab — above the first month, since it draws from the past the
          Year no longer shows a ribbon for. Draws nothing once its own
          queue is empty. */}
      <RateQueue />

      {displayMonths.map(ym => (
        <MonthRibbon
          key={ym} ym={ym} lo={today} today={today}
          periods={displayPeriods} visitors={visitors} items={displayItems}
          onDayTap={setDaySheet} onEditPeriod={onEditPeriod} onEditItem={onEditItem} onRemoveItem={onRemoveItem}
          onEditVisitor={onEditVisitor}
        />
      ))}

      <YearSummaryCard
        year={nextYearNum} stays={stays} periods={periods(stays, `${nextYearNum}-01-01`, `${nextYearNum}-12-31`)}
        items={displayItems.filter(i => i.on.startsWith(`${nextYearNum}-`))}
        leave={leaveFor(nextYearNum)}
        expanded={show2027} onToggle={() => setShow2027(s => !s)}
      />

      {y2027Slots.map(slot => {
        if (slot.kind === 'quiet') {
          const n = birthdaysIn(slot.months)
          return (
            <button
              key={slot.months[0]} type="button" className="yr-quiet"
              onClick={() => setOpenedQuiet(prev => new Set([...prev, ...slot.months]))}
            >
              <span className="yr-quiet__span">{monthSpan(slot.months)}</span>
              <span className="yr-quiet__note">
                <span>nothing planned</span>
                {n > 0 && <>{' · '}<span>{n} birthday{n === 1 ? '' : 's'}</span></>}
              </span>
              <ChevronDown size={16} aria-hidden="true" />
            </button>
          )
        }
        const ym = slot.ym
        return (
          <MonthRibbon
            key={ym} ym={ym} lo={today} today={today}
            periods={displayPeriods} visitors={visitors} items={displayItems}
            onDayTap={setDaySheet} onEditPeriod={onEditPeriod} onEditItem={onEditItem} onRemoveItem={onRemoveItem}
            onEditVisitor={onEditVisitor}
          />
        )
      })}

      {daySheet && (
        <DaySheet
          day={daySheet} periods={displayPeriods} visitors={visitors} items={displayItems.filter(i => i.on === daySheet)}
          onEditItem={onEditItem}
          onClose={() => setDaySheet(null)}
          onAdd={() => { setDaySheet(null); setSheet('add') }}
        />
      )}

      <EventSheet e={openEntry} onClose={() => setOpenEntry(null)} />

      {addSheet}

      {edit?.kind === 'stay' && (
        <StayForm today={today} maxDate={addMax} initial={edit.row} stays={stays} onClose={() => setEdit(null)}
          onSave={s => { markLeave(edit.row, s); void afterEdit(updateStayM.mutateAsync({ id: edit.row.id, patch: s })) }}
          onDelete={() => { removeStay(edit.row); setEdit(null) }} />
      )}
      {edit?.kind === 'commitment' && (
        <CommitmentForm today={today} maxDate={addMax} initial={edit.row} onClose={() => setEdit(null)}
          onSave={(date, title) => void afterEdit(updateCommitmentM.mutateAsync({ id: edit.row.id, patch: { on_date: date, title } }))}
          onDelete={() => { removeCommitment(edit.row); setEdit(null) }} />
      )}
      {edit?.kind === 'flight' && (
        <FlightForm today={today} maxDate={addMax} initial={edit.row} onClose={() => setEdit(null)}
          onSave={f => void afterEdit(updateFlightM.mutateAsync({ id: edit.row.id, patch: f }))}
          onDelete={() => { removeFlight(edit.row); setEdit(null) }} />
      )}
      {edit?.kind === 'visitor' && (
        <VisitorForm today={today} maxDate={addMax} initial={edit.row} onClose={() => setEdit(null)}
          onSave={v => void afterEdit(updateVisitorM.mutateAsync({ id: edit.row.id, patch: v }))}
          onDelete={() => { removeVisitor(edit.row); setEdit(null) }} />
      )}
    </>
  )
}

/**
 * 2027, one card — twelve month cells (away/Berlin/none), stays planned,
 * birthdays and leave days — until it opens month by month (rule 4). The
 * link toggles that for now, either direction.
 */
function YearSummaryCard({ year, stays, periods: yearPeriods, items, leave, expanded, onToggle }: {
  year: number
  stays: Stay[]
  periods: Period[]
  items: YearItem[]
  leave: { free: number; over: number } | undefined
  expanded: boolean
  onToggle: () => void
}) {
  const yearStays = stays.filter(s => s.starts_on.startsWith(`${year}-`))
  const birthdayCount = items.filter(i => i.kind === 'birthday').length
  const cells = []
  for (let m = 1; m <= 12; m++) {
    const ym = `${year}-${String(m).padStart(2, '0')}`
    const first = `${ym}-01`
    const last = new Date(Date.UTC(year, m, 0)).toISOString().slice(0, 10)
    const away = yearPeriods.some(p => p.place !== 'Home' && p.start <= last && p.end >= first)
    const n = items.filter(i => i.on >= first && i.on <= last).length
    const cls = away ? 'Away' : (yearStays.length || items.length ? 'Home' : 'none')
    cells.push(
      <span key={ym} className={`mc ${cls}`} title={fmt(first, { month: 'long' })}>
        <span className="mc__m">{fmt(first, { month: 'short' }).slice(0, 1)}</span>
        {n > 0 && <b>{n}</b>}
      </span>,
    )
  }
  return (
    <section className="yr">
      <h3>{year}<span>{expanded ? '' : 'opens in full from 1 October'}</span></h3>
      <div className="mstrip">{cells}</div>
      {/* Each clause its own `nowrap` span, as `.ystats` does: at 390px the
          line wraps between clauses, at a " · ", never inside one. */}
      <div className="yr-len yr-len--clauses">
        <span>{yearStays.length ? <><b>{yearStays.length} stay{yearStays.length === 1 ? '' : 's'}</b> planned</> : <><b>No stays</b> planned yet</>}</span>
        {' · '}<span>{birthdayCount} birthdays on the calendar</span>
        {leave && <>{' · '}<span>{leave.over > 0
          ? <b>{leave.over} leave {leave.over === 1 ? 'day' : 'days'} over</b>
          : <b>{leave.free} leave days</b>}</span></>}
      </div>
      <Button variant="quiet" size="sm" className="yr__toggle" onClick={onToggle}>
        {expanded ? `Collapse ${year}` : `Show ${year} month by month`}
      </Button>
    </section>
  )
}

/** A day tapped on the ribbon: where Doug is, the holiday, who is visiting,
 *  what is on, and a way to add something here. Ported from the
 *  prototype's `dayPopover`. */
function DaySheet({ day, periods: allPeriods, visitors, items, onEditItem, onClose, onAdd }: {
  day: string
  periods: Period[]
  visitors: Visitor[]
  items: YearItem[]
  /** WP 8.5: the same rule `PeriodCard`'s rows apply — a commitment or a
   *  flight opens (`onEditItem` itself decides the sheet vs. the plain
   *  form); a birthday, holiday or saved event has nothing here to open. */
  onEditItem: (item: YearItem) => void
  onClose: () => void
  onAdd: () => void
}) {
  const profile = useProfile()
  const p = placeOf(allPeriods, day)
  const place = p?.place ?? 'Home'
  const home = homeName(useRegionsNow(), profile)
  const holiday = holidayOn(day, place === 'Home2' ? 'Home2' : 'Home', secondHomeCountry(profile), useRegionHolidays())
  const here = visitors.filter(v => day >= v.starts_on && day <= v.ends_on)
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={fmt(day, { weekday: 'long', day: 'numeric', month: 'long' })}
      actions={
        <>
          <Button variant="primary" onClick={onAdd}>Add on this day</Button>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </>
      }>
      <p className="yr-len">
        {p ? placeName(p, secondHomeOf(profile), home) : home}
        {holiday && <> · <b>{holiday}</b></>}
        {here.length > 0 && <> · {here.map(v => v.who).join(', ')} visiting</>}
      </p>
      {items.length > 0 ? (
        <ul className="items">
          {items.map(i => {
            const editable = i.kind === 'commitment' || i.kind === 'flight'
            const body = (
              <>
                <span className="t">{i.title}</span>
                <span className="x">{i.fact ?? ''}</span>
              </>
            )
            return (
              <li key={i.id} className="it">
                {editable
                  ? <button type="button" className="it__btn" onClick={() => onEditItem(i)}>{body}</button>
                  : body}
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="yr-len">Nothing on this day</p>
      )}
    </Sheet>
  )
}

function CommitmentForm({ today, maxDate, initial, onClose, onSave, onDelete }: {
  today: string; maxDate: string; initial?: Commitment; onClose: () => void; onSave: (d: string, t: string) => void
  onDelete?: () => void
}) {
  const [date, setDate] = useState(initial?.on_date ?? today)
  const [title, setTitle] = useState(initial?.title ?? '')
  // From 1 January of the day's own year (a2-26) — the day being corrected
  // is often already in the past.
  const dateMin = yearFormMin(today, initial?.on_date)
  const dateMax = initial ? undefined : maxDate
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={initial ? 'Edit the day' : 'Add a day'}>
      <FormColumn submitLabel={initial ? 'Save' : 'Add'} canSubmit={!!title.trim()}
        onSubmit={() => onSave(date, title.trim())} onCancel={onClose} onDelete={onDelete}>
        <Field label="Date"><Field.Input type="date" value={date} min={dateMin} max={dateMax}
               onChange={e => setDate(e.target.value)} /></Field>
        <Field label="What"><Field.Input value={title} placeholder="Duck Fest" onChange={e => setTitle(e.target.value)} /></Field>
      </FormColumn>
    </Sheet>
  )
}

function StayForm({ today, maxDate, initial, stays, onClose, onSave, onDelete }: {
  today: string; maxDate: string; initial?: Stay; stays: Stay[]; onClose: () => void
  onSave: (s: { kind: 'home2' | 'travel'; starts_on: string; ends_on: string; label: string | null; provisional: boolean; country: string | null }) => void
  onDelete?: () => void
}) {
  // a2-25: the add sheet's fields (`StayFields`), so the two forms cannot
  // drift — Type, Place, and Country filled from the place.
  const second = secondHomeOf(useProfile())
  const [d, setD] = useState(() => stayDraft(today, initial, second ? 'home2' : 'travel'))
  const learned = useMemo(() => learnedCountries(stays), [stays])
  // From 1 January of the stay's own year (a2-26): 2025's trips are all in
  // the past, and correcting one should not be blocked by it.
  const dateMin = yearFormMin(today, initial?.starts_on)
  const dateMax = initial ? undefined : maxDate
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={initial ? 'Edit stay or trip' : 'Add a stay or trip'}>
      <FormColumn submitLabel={initial ? 'Save' : 'Add'} onCancel={onClose} onDelete={onDelete}
        onSubmit={() => onSave(stayFromDraft(d))}>
        <StayFields d={d} onChange={setD} second={second} learned={learned} dateMin={dateMin} dateMax={dateMax}
          toMin={initial ? undefined : d.start} />
      </FormColumn>
    </Sheet>
  )
}

function FlightForm({ today, maxDate, initial, onClose, onSave, onDelete }: {
  today: string; maxDate: string; initial?: Flight; onClose: () => void
  onSave: (f: { on_date: string; depart_at: string | null; from_iata: string | null; to_iata: string | null; code: string }) => void
  onDelete?: () => void
}) {
  const [date, setDate] = useState(initial?.on_date ?? today)
  // a2-27: an emailed BER → VNO used to open on a direction the select did
  // not offer; `formFromRoute` reads any stored route as one it does.
  const profile = useProfile()
  const regions = useRegionsNow()
  const airports = secondHomeAirports(profile)
  const second = secondHomeOf(profile)
  const route = initial
    ? formFromRoute(initial.from_iata, initial.to_iata, { homeIata: profile.home_iata, secondAirport: airports[0] ?? null })
    : { dir: defaultDirection(profile.home_iata, airports), where: '' }
  const [dir, setDir] = useState<Direction>(route.dir)
  const [where, setWhere] = useState(route.where)
  const [time, setTime] = useState(initial?.depart_at?.slice(0, 5) ?? '')
  const [code, setCode] = useState(initial?.code ?? '')
  const dateMin = yearFormMin(today, initial?.on_date)
  const dateMax = initial ? undefined : maxDate
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={initial ? 'Edit flight' : 'Add a flight'}>
      <FormColumn submitLabel={initial ? 'Save' : 'Add flight'} onCancel={onClose} onDelete={onDelete}
        onSubmit={() => {
          onSave({ on_date: date, depart_at: time || null, ...routeFromForm(dir, where), code: code.trim() || 'Flight' })
        }}>
        <Field label="Date"><Field.Input type="date" value={date} min={dateMin} max={dateMax} onChange={e => setDate(e.target.value)} /></Field>
        <Field label="Direction">
          <Field.Select value={dir} onChange={v => setDir(v as Direction)}
            options={directionsFor({ homeIata: profile.home_iata, secondHome: second, homeName: homeName(regions, profile) })} />
        </Field>
        {dir.includes('*') && (
          <Field label="Where"><Field.Input value={where} placeholder="Lisbon" onChange={e => setWhere(e.target.value)} /></Field>
        )}
        <Field label="Time"><Field.Input type="time" value={time} onChange={e => setTime(e.target.value)} /></Field>
        <Field label="Flight number"><Field.Input value={code} placeholder="FR 1234" onChange={e => setCode(e.target.value)} /></Field>
      </FormColumn>
    </Sheet>
  )
}

function VisitorForm({ today, maxDate, initial, onClose, onSave, onDelete }: {
  today: string; maxDate: string; initial?: Visitor; onClose: () => void
  onSave: (v: { who: string; starts_on: string; ends_on: string; at_mine: boolean }) => void
  onDelete?: () => void
}) {
  const [who, setWho] = useState(initial?.who ?? '')
  const [start, setStart] = useState(initial?.starts_on ?? today)
  const [end, setEnd] = useState(initial?.ends_on ?? today)
  const [staying, setStaying] = useState<'mine' | 'else'>(initial ? (initial.at_mine ? 'mine' : 'else') : 'mine')
  const dateMin = yearFormMin(today, initial?.starts_on)
  const dateMax = initial ? undefined : maxDate
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={initial ? 'Edit visitor' : 'Add a visitor'}>
      <FormColumn submitLabel={initial ? 'Save' : 'Add'} canSubmit={!!who.trim()} onCancel={onClose} onDelete={onDelete}
        onSubmit={() => onSave({
          who: who.trim(),
          starts_on: start <= end ? start : end,
          ends_on: start <= end ? end : start,
          at_mine: staying === 'mine',
        })}>
        <Field label="Who"><Field.Input value={who} placeholder="Rachel" onChange={e => setWho(e.target.value)} /></Field>
        <Field label="Arrives"><Field.Input type="date" value={start} min={dateMin} max={dateMax} onChange={e => setStart(e.target.value)} /></Field>
        <Field label="Leaves"><Field.Input type="date" value={end} min={initial ? undefined : start} max={dateMax} onChange={e => setEnd(e.target.value)} /></Field>
        <Field label="Staying">
          <Field.Select value={staying} onChange={v => setStaying(v as typeof staying)} options={[
            { value: 'mine', label: 'At yours' },
            { value: 'else', label: 'Elsewhere' },
          ]} />
        </Field>
      </FormColumn>
    </Sheet>
  )
}
